import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, setStoredToken } from "./AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const { setUser, checkAuth } = useAuth();
  const [error, setError] = useState(null);
  const [stage, setStage] = useState("Reading session…");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/");
      return;
    }
    const sessionId = match[1];

    (async () => {
      try {
        setStage("Verifying with Google…");
        const r = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId }
        );
        // Store token as Bearer fallback (in case browser blocks 3rd-party cookies)
        if (r.data.session_token) {
          setStoredToken(r.data.session_token);
        }
        setUser(r.data.user);
        window.history.replaceState({}, "", window.location.pathname);
        setStage("Loading your snapshot…");
        await checkAuth();
        const meResp = await axios.get(`${API}/auth/me`);
        if (meResp.data.onboarding?.completed) {
          navigate("/dashboard", { state: { user: meResp.data.user } });
        } else if (meResp.data.onboarding?.mentor_id) {
          // Mentor picked but onboarding incomplete → resume conversation
          navigate("/welcome", { state: { user: meResp.data.user } });
        } else {
          // Fresh user → mentor selection first
          navigate("/mentor", { state: { user: meResp.data.user } });
        }
      } catch (e) {
        console.error("Auth callback failed", e);
        const msg =
          e?.response?.data?.detail ||
          e?.message ||
          "Sign-in failed. Please try again.";
        setError(`${msg}${e?.response?.status ? ` (status ${e.response.status})` : ""}`);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <p className="font-mono-eyebrow text-[#b94e4e] mb-3">Sign-in error</p>
          <h1 className="font-display text-[28px] text-[#0f1724] tracking-tight leading-tight mb-4">
            We couldn't complete sign-in.
          </h1>
          <p className="font-body text-[14px] text-[#4b5563] leading-relaxed mb-6 break-words">
            {error}
          </p>
          <button
            onClick={() => (window.location.href = "/start")}
            className="px-6 py-3 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[14px] btn-lift"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-6 border-4 border-[#e6dcc6] border-t-[#486a8f] rounded-full animate-spin" />
        <p className="font-body text-[#4b5563] text-sm">{stage}</p>
      </div>
    </div>
  );
}
